import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import _ from "lodash";
import { AuthContext } from "context/AuthContext";
import AlertsByDate from "./alerts-by-date";
import { AlertsContext } from "context/AlertsContext";
import { AlertsControlBarContext } from "context/AlertsControlBarContext";
import moment from "moment";
import PubSub from "pubsub-js";
import { formatContextDate } from "lib/utils";
import { Select, Empty } from "antd";
import { throttle } from "lodash";
import { USER_ROLE } from "lib/constants";
import "./alerts-list.scss";
import { AlertPageMode } from "lib/constants";
import { showToast } from "ui/components/common";
import { getAlertAndEventByGpt, getAlertsByStaticParams } from "./service";
import { getVehicleList } from "pages/dashboard/homePage/service";
import dayjs from "dayjs";
import { SignalContext } from "context/SignalContext";
import {
  subscribeToAlertQueue,
  unsubscribeFromAlertQueue,
} from "pages/dashboard/alertQueue/service";

const options = [
  {
    value: "off",
    label: "off",
  },
  {
    value: "15",
    label: "15s",
  },
  {
    value: "30",
    label: "30s",
  },
  {
    value: "60",
    label: "60s",
  },
];
export const RelevanceOptions = [
  {
    value: 0,
    label: "More Accurate",
  },
  {
    value: -1,
    label: "Normal",
  },
  {
    value: -2,
    label: "More Results",
  },
];
const eventOptions = [
  { value: 1, label: "Only Alerts" },
  { value: 0, label: "Events + Alerts" },
];
let lastQuery = "";
export default function AlertsList({ mode }) {
  useEffect(() => {
    lastQuery = "";
  }, []);
  const { user } = useContext(AuthContext);
  const {
    eventTypes,
    siteIds,
    cameraIds,
    startedAt,
    endedAt,
    read,
    statuses,
    personCoatColors,
    personTrousersColors,
    vehicleColors,
    nonMotorVehiclePersonCoatColors,
    vehiclePlateNos,
    gptText,
    setVideoUrl,
    vehicleSearchParams,
    searchType: gptSearchType,
    alertSound,
  } = useContext(AlertsContext);

  const { setHasNextAlert, setHasPreviousAlert, setActiveAlert, activeAlert } =
    useContext(AlertsControlBarContext);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [lastKey, setLastKey] = useState(null);
  const [alertData, setAlertData] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState<string>("off");
  const [autoRefreshSignal, setAutoRefreshSignal] = useState(0);
  const [gptLoading, setGptLoading] = useState(false);
  const [relevanceThreshold, setRelevanceThreshold] = useState(-1);
  const [searchFlag, setSearchFlag] = useState(0);
  const [searchType, setSearchType] = useState("static");
  const [isLoading, setIsLoading] = useState(false);
  const { wsMsgData, sessionId } = useContext(SignalContext);
  useEffect(() => {
    if (!sessionId) return;
    subscribeToAlertQueue(sessionId);
    // auto unsub when websocket is disconnected
    // return () => {
    //   unsubscribeFromAlertQueue(sessionId);
    // };
  }, [sessionId]);
  useEffect(() => {
    if (
      mode !== AlertPageMode.Queue ||
      wsMsgData?.result?.act !== "ai.queue.notify_change" ||
      user?.id.toString() !==
        wsMsgData?.result?.data?.aiQueueNotifyChange?.operatorId.toString() ||
      !wsMsgData?.result?.data?.aiQueueNotifyChange?.changed
    ) {
      return;
    }
    getAlertsWithParams(false);
  }, [wsMsgData, mode]);
  const latestAlertTimeRef = useRef(0);
  const handleFilter = () => {
    const newFilters = {
      eventTypes,
      siteIds,
      cameraIds,
      startedAt,
      endedAt,
      read,
      statuses,
      personCoatColors,
      personTrousersColors,
      vehicleColors,
      nonMotorVehiclePersonCoatColors,
      vehiclePlateNos,
      searchFlag,
    };
    return newFilters;
  };
  async function getAlertsWithParams(append) {
    // update latestAlertTimeRef if there is a new alert
    const hasNewAlert = checkHasNewAlert(alertData?.at(0));
    if (hasNewAlert) {
      latestAlertTimeRef.current = +alertData?.at(0)?.createdAt;
    }
    const filters = handleFilter();
    const addtion =
      user.role === USER_ROLE.Inspector ||
      user.role == USER_ROLE.Dispatcher ||
      user.role == USER_ROLE.Operator
        ? `&withCustomer=true`
        : "";
    const queryString =
      Object.keys(filters)
        .filter((key) => {
          if (key === "read" && filters[key] === "false") {
            return false;
          }
          return filters[key] !== "";
        })
        .map((key) => {
          let value = filters[key];
          if (key === "startedAt" || key === "endedAt") {
            const date = new Date(value);
            value = date.getTime();
          }
          if (key === "read" && value === "true") {
            value = "false";
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join("&") +
      (lastKey && append ? `&paging.last=${lastKey}` : "") +
      "&paging.limit=10";
    if (lastQuery === queryString && append === true) return;
    lastQuery = queryString;
    setIsLoading(true);
    setSearchType("static");
    try {
      const res = await getAlertsByStaticParams(
        `${queryString}${addtion}`,
        mode === AlertPageMode.Queue,
      );
      setIsLoading(false);
      if (res.status !== 200) {
        showToast(1, "Failed to fetch alerts. Please try again later.");
      } else {
        setHasNext(res.data.paging?.hasNext);
        if (append) {
          setAlertData((prev) => [...prev, ...res.data.data]);
        } else {
          setAlertData([...res.data.data]);
        }

        setLastKey(res.data.paging.last);
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    audioRef.current = new Audio("/chime.mp3");
  }, []);

  async function getAlertWithGpt() {
    const filters = handleFilter();
    setGptLoading(true);
    setAlertData([]);
    setLastKey(null); // Reset pagination when filters change
    setSearchType("gpt");

    if (gptSearchType === "magic" || gptSearchType === "magicV2") {
      const res = await getAlertAndEventByGpt(
        gptText,
        relevanceThreshold,
        true,
        filters,
        null,
        gptSearchType,
        AlertsList.name + "Magic Search",
      );
      //@ts-ignore
      if (res.code === "ERR_CANCELED") {
        console.log("Request cancelled");
        return;
      }
      setGptLoading(false);
      if (res?.status === 200) setAlertData(res.data.data);
    } else {
      let startedAt, endedAt;
      if (filters.startedAt) {
        startedAt = dayjs(filters.startedAt).valueOf();
        endedAt = dayjs(filters.endedAt).valueOf();
      }

      const res = await getVehicleList(
        {
          ...vehicleSearchParams,
          ...filters,
          startedAt,
          endedAt,
        },
        AlertsList.name + "Magic Search",
      );
      //@ts-ignore
      if (res.code === "ERR_CANCELED") {
        return;
      }
      setGptLoading(false);
      if (res?.status === 200) {
        const alerts = res.data.alerts;
        const alertListwithCars = res.data.cars.map((item) => {
          const matchAlert = _.cloneDeep(alerts[item.alertId]);
          matchAlert.matchedVehicle = item.car;
          return matchAlert;
        });
        setAlertData(alertListwithCars);
      }
    }
  }
  useEffect(() => {
    if (gptText) {
      getAlertWithGpt();
    }
  }, [relevanceThreshold, searchFlag]);
  useEffect(() => {
    if (
      scrollOffset >= 0 &&
      scrollOffset < 5 &&
      hasNext &&
      searchType === "static"
    ) {
      handleLoadMore();
    }
  }, [scrollOffset]);

  useEffect(() => {
    if (mode === AlertPageMode.Focus && autoRefresh === "off") {
      setAutoRefresh("15");
    }
    if (mode === AlertPageMode.Queue) {
      setAutoRefresh("off");
    }
  }, [mode]);
  const handleLoadMore = () => {
    getAlertsWithParams(true);
  };

  const millisecondsToDate = (milliseconds: string) => {
    return moment(parseInt(milliseconds)).format("YYYY/MM/DD");
  };

  const checkHasNewAlert = (newAlert) => {
    if (!newAlert || !newAlert.createdAt) return false;

    if (+newAlert.createdAt > latestAlertTimeRef.current) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!alertData.length) return;
    const hasNewAlert = checkHasNewAlert(alertData?.at(0));
    if (hasNewAlert && alertSound && latestAlertTimeRef.current) {
      audioRef.current.volume = 1;
      audioRef.current.play().catch(() => {
        console.warn("Audio playback was prevented by the browser.");
      });
    }

    const activeAlertExistInList = alertData.find(
      (alert) => alert.id === activeAlert?.id,
    );
    if (!activeAlert || !activeAlertExistInList) {
      setActiveAlert(alertData?.at(0));
      setVideoUrl(alertData?.at(0)?.video?.url);
    }
  }, [alertData]);

  const groupedAlerts = useMemo(() => {
    if (gptText) {
      return { noGroup: alertData };
    }
    if (mode === AlertPageMode.Basic) {
      return alertData.reduce((groups, alert) => {
        const dateKey = formatContextDate(millisecondsToDate(alert.createdAt), {
          month: "short",
          day: "2-digit",
        });

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(alert);
        return groups;
      }, {});
    } else if (mode === AlertPageMode.Focus || mode === AlertPageMode.Queue) {
      return alertData.reduce((groups, alert) => {
        const dateKey = alert?.site?.name;
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(alert);
        return groups;
      }, {});
    }
  }, [alertData, mode]);

  const handleChangeUser = () => {
    setAlertData([]);
    setHasNext(false);
    setLastKey(null);
    //mutate(`/alert/alerts?${queryString}`);
  };

  useEffect(() => {
    console.log("new user");
    handleChangeUser();
  }, [user.id]);

  useEffect(() => {
    if (autoRefresh === "off" || searchType === "gpt") {
      return;
    }

    const intervalId = setInterval(
      async () => {
        setAutoRefreshSignal((prev) => prev + 1);
      },
      parseInt(autoRefresh) * 1000,
    );
    return () => clearInterval(intervalId);
  }, [autoRefresh, searchType]);

  useEffect(() => {
    if (autoRefresh === "off") {
      return;
    }
    setAlertData([]);
    setHasNext(false);
    setLastKey(null);
    getAlertsWithParams(false);
  }, [autoRefreshSignal]);

  useEffect(() => {
    let nextAlertClicked = () => {};
    let previousAlertClicked = () => {};
    Object.entries(groupedAlerts).find(([_, d]) => {
      const alerts = d as {
        id: string;
        video: { url: string };
        createdAt: string;
      }[];
      const index = alerts.findIndex((alert, index) => {
        if (alert.id === activeAlert?.id) {
          console.log("Alert found", alert, index);
          nextAlertClicked = () => {
            const curAlert = alerts[index + 1];
            if (index < alerts.length - 1) {
              Object.assign(curAlert, {
                alertTime: formatContextDate(
                  new Date(Number(curAlert?.createdAt)),
                ),
                startTime: curAlert?.createdAt,
              });
              setActiveAlert(curAlert);
              setVideoUrl(curAlert?.video?.url);
            }
          };
          previousAlertClicked = () => {
            const curAlert = alerts[index - 1];
            if (index > 0) {
              Object.assign(curAlert, {
                alertTime: formatContextDate(
                  new Date(Number(curAlert?.createdAt)),
                ),
                startTime: curAlert?.createdAt,
              });
              setActiveAlert(curAlert);
              setVideoUrl(curAlert?.video?.url);
            }
          };
          return true;
        }
        return false;
      });
      if (index >= 0) {
        return true;
      }
      return false;
    });
    const handleChangeAlertStatusInList = ({ id, status, tag }) => {
      Object.entries(groupedAlerts).forEach(([_, alerts]: Array<any>) => {
        const alert = alerts.find((alert) => alert.id === id);
        alert && status && (alert.status = status);
        alert && tag && (alert.tag = tag);
      });
    };
    const subChangeAlertStatus = PubSub.subscribe(
      "changeAlertStatusInList",
      handleChangeAlertStatusInList,
    );
    const subNextAlert = PubSub.subscribe("nextAlertClicked", nextAlertClicked);
    const subpreviousAlert = PubSub.subscribe(
      "previousAlertClicked",
      previousAlertClicked,
    );
    return () => {
      PubSub.unsubscribe("nextAlertClicked", subNextAlert);
      PubSub.unsubscribe("previousAlertClicked", subpreviousAlert);
      PubSub.unsubscribe("changeAlertStatusInList", subChangeAlertStatus);
    };
  }, [groupedAlerts, activeAlert]);
  useEffect(() => {
    if (Object.keys(groupedAlerts).length === 0) {
      setHasNextAlert(false);
      setHasPreviousAlert(false);
      return;
    }
    Object.entries(groupedAlerts).find(([_, d]) => {
      const alerts = d as { id: string }[];
      const index = alerts.findIndex((alert, index) => {
        if (alert.id === activeAlert?.id) {
          console.log("Alert found", alert, index);
          if (index === 0) {
            setHasPreviousAlert(false);
          } else {
            setHasPreviousAlert(true);
          }
          if (index === alerts.length - 1) {
            setHasNextAlert(false);
          } else {
            setHasNextAlert(true);
          }
          return true;
        }
        return false;
      });
      if (index >= 0) {
        return true;
      }
      setHasNextAlert(false);
      setHasPreviousAlert(false);
      return false;
    });
  }, [activeAlert, groupedAlerts]);

  const onScroll = useCallback(
    throttle((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      setScrollOffset(scrollHeight - scrollTop - clientHeight);
    }, 200),
    [],
  );
  //TODO remove context to make search called only once when initial load
  useEffect(() => {
    setLastKey(null);
    setHasNext(false);
    setAlertData([]);
    if (
      (gptText && gptSearchType === "magic") ||
      (gptText && gptSearchType === "magicV2") ||
      gptSearchType === "lpr"
    ) {
      getAlertWithGpt();
    } else {
      getAlertsWithParams(false);
    }
  }, [
    vehicleSearchParams,
    gptText,
    eventTypes,
    siteIds,
    cameraIds,
    startedAt,
    endedAt,
    read,
    statuses,
    personCoatColors,
    personTrousersColors,
    vehicleColors,
    nonMotorVehiclePersonCoatColors,
    vehiclePlateNos,
    gptSearchType,
  ]);

  return (
    <div className="alert-list-container">
      <audio ref={audioRef} />
      {mode !== AlertPageMode.Queue && (
        <div className="flex justify-between items-center mb-4 mt-6 w-[120px] pl-6">
          {/* <span className="ml-6 mr-2 text-nowrap">Auto Refresh</span> */}
          {searchType === "static" && (
            <Select
              className="drawer-select mr-2 "
              popupClassName="select-dropdown"
              dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
              value={autoRefresh}
              onChange={(value) => setAutoRefresh(value)}
              options={options}
            />
          )}

          {(gptSearchType === "magic" || gptSearchType === "magicV2") && (
            <>
              <Select
                className="drawer-select mr-2"
                popupClassName="select-dropdown relevance"
                dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                value={relevanceThreshold}
                onChange={(value) => setRelevanceThreshold(value)}
                options={RelevanceOptions}
              />

              <Select
                className="drawer-select"
                popupClassName="select-dropdown relevance"
                dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                value={searchFlag}
                onChange={(value) => setSearchFlag(value)}
                options={eventOptions}
              />
            </>
          )}
        </div>
      )}

      <div
        style={{
          maxHeight:
            mode === AlertPageMode.Queue
              ? "calc(100vh - 130px)"
              : "calc(100vh - 230px)",
          overflowY: "auto",
        }}
        onScroll={onScroll}
      >
        {Object.entries(groupedAlerts).map(([date, alerts]) => (
          <AlertsByDate
            key={date}
            date={date}
            alerts={alerts}
            setVideoUrl={setVideoUrl}
            activeAlert={activeAlert}
            setActiveAlert={setActiveAlert}
            latestAlertTime={latestAlertTimeRef.current}
          />
        ))}
        {searchType === "gpt" && gptLoading && (
          <div className="w-full text-center mt-4">
            <button
              onClick={handleLoadMore}
              disabled={true}
              className="rounded-xl bg-white w-[104px] h-10 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              <div
                className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            </button>
          </div>
        )}
        {searchType === "gpt" && !gptLoading && alertData?.length === 0 && (
          <p className="text-lg text-center text-neutral-500 pb-3 pt-1">
            No Alerts Found
          </p>
        )}
        {searchType === "static" && hasNext && (
          <div className="w-full text-center mt-4">
            <button
              onClick={handleLoadMore}
              disabled={isLoading || gptLoading}
              className="rounded-xl bg-white w-[104px] h-10 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              {isLoading || gptLoading ? (
                <div
                  className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                "Load More"
              )}
            </button>
          </div>
        )}
      </div>
      {searchType === "static" &&
        !isLoading &&
        !hasNext &&
        alertData?.length == 0 && (
          <div className="mt-6">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No Alerts Found"
            />
          </div>
        )}
    </div>
  );
}
